import { create } from 'zustand'
import axios from 'axios'
import { ASSISTENT_API_URL } from '@/config/backend'
import { useAuthStore } from './auth-store'
import { logger } from '@/lib/logger'

interface Device {
  id: string
  deviceId: string
  customerName?: string  // Name from API
  name?: string          // Fallback
  type?: string
  usecaseTypeId?: number
  enabled?: boolean
  customerId?: number
  deviceConfiguration?: Record<string, string | number | boolean>
}

interface AutomaticSmsConfiguration {
  enabled: boolean
  phoneNumber?: string
  message?: string
}

interface DevicesState {
  currentDeviceId: string | null
  currentDeviceSerialId: string | null
  currentDevice: Device | null
  availableDevices: Device[]
  automaticSmsConfiguration: AutomaticSmsConfiguration | null

  // Actions
  initializeDevices: () => Promise<void>
  setSelectedDevice: (selectedDevice: Device, serialId: string) => Promise<void>
  getAutomaticSmsConfiguration: () => Promise<AutomaticSmsConfiguration>
  resetAutomaticSmsConfiguration: () => void
  updateConfigData: (key: string, value: string | number | boolean) => Promise<void>
  findDeviceByDeviceId: (deviceId: string) => Device | null
  resetState: () => void
  getAvailableDevices: () => Device[]

  // Getters
  isDeviceSelected: () => boolean
  isMdsDevice: () => boolean
  getCurrentDeviceSerialId: () => string | null
  getData: (key: string) => string | number | boolean | null
  getCurrentDeviceId: () => string | null
  getCurrentDevice: () => Device | null
  currentDeviceName: () => string
}

// Store integration helpers
const getAuthHeaders = () => {
  return useAuthStore.getState().getApiHeaders()
}

const showAlertByKey = (key: string) => {
  console.error('Alert:', key)
  // This will be replaced with proper toast notifications
}

export const useDevicesStore = create<DevicesState>((set, get) => ({
  currentDeviceId: null,
  currentDeviceSerialId: null,
  currentDevice: null,
  availableDevices: [],
  automaticSmsConfiguration: null,

  initializeDevices: async () => {
    const { availableDevices, setSelectedDevice } = get()
    
    if (availableDevices.length === 0) {
      const url = `${ASSISTENT_API_URL}devices`
      
      logger.devices('Loading devices from API', { url })
      
      try {
        const response = await axios.get(url, {
          headers: getAuthHeaders()
        })

        const loadedDevices = response.data.devices || []
        logger.devices('Devices loaded successfully', { count: loadedDevices.length })
        
        set({ availableDevices: loadedDevices })

        // Auto-select first device (always!)
        if (loadedDevices.length > 0) {
          const firstDevice = loadedDevices[0]
          logger.devices('Auto-selecting first device after login', { 
            deviceId: firstDevice.deviceId,
            name: firstDevice.name || firstDevice.customerName 
          })
          await setSelectedDevice(firstDevice, firstDevice.deviceId)
        } else {
          logger.warn('No devices available for user')
        }
      } catch (error: any) {
        logger.error('Failed to load devices', error, {
          status: error.response?.status,
          message: error.message
        })
        throw new Error('UNKNOWN_ERROR')
      }
    } else {
      logger.devices('Devices already loaded', { count: availableDevices.length })
    }
  },

  setSelectedDevice: async (selectedDevice: Device, serialId: string) => {
    const { currentDeviceSerialId } = get()
    const updated = serialId !== currentDeviceSerialId

    logger.devices('Setting selected device', {
      deviceId: serialId,
      name: selectedDevice.name || selectedDevice.customerName,
      isUpdate: updated
    })

    set({
      currentDeviceId: selectedDevice.id,
      currentDeviceSerialId: serialId,
      currentDevice: selectedDevice
    })

    localStorage.setItem("deviceId", serialId)
    localStorage.setItem("currentDeviceSerialId", serialId)

    if (updated) {
      logger.devices('Device changed - resetting stores and loading data')
      
      try {
        const [reportingModule, customerModule] = await Promise.all([
          import('./reporting-store'),
          import('./customer-store')
        ])
        
        reportingModule.useReportingStore.getState().reset()
        await customerModule.useCustomerStore.getState().reset()
        
        logger.devices('Stores reset and data loaded successfully')
      } catch (error) {
        logger.error('Failed to reset stores after device selection', error)
      }
    }
  },

  getAutomaticSmsConfiguration: async () => {
    const { automaticSmsConfiguration, currentDeviceSerialId } = get()
    
    if (automaticSmsConfiguration) {
      return automaticSmsConfiguration
    }

    const url = `${ASSISTENT_API_URL}device/${currentDeviceSerialId}/sms/automatic`

    try {
      const response = await axios.get(url, {
        headers: getAuthHeaders()
      })

      const config = response.data.resultObject
      set({ automaticSmsConfiguration: config })
      return config
    } catch (error) {
      showAlertByKey("error.unknown")
      throw new Error('UNKNOWN_ERROR')
    }
  },

  resetAutomaticSmsConfiguration: () => {
    set({ automaticSmsConfiguration: null })
  },

  updateConfigData: async (key: string, value: string | number | boolean) => {
    const { currentDevice, currentDeviceSerialId } = get()
    
    if (currentDevice) {
      // Update local state
      const updatedDevice = {
        ...currentDevice,
        deviceConfiguration: {
          ...currentDevice.deviceConfiguration,
          [key]: value
        }
      }
      set({ currentDevice: updatedDevice })

      // Update on server
      const url = `${ASSISTENT_API_URL}configuration/basic/${currentDeviceSerialId}`
      const putBody = { key, value }

      return axios.put(url, putBody, {
        headers: getAuthHeaders()
      })
    }
  },

  findDeviceByDeviceId: (deviceId: string) => {
    const { availableDevices } = get()
    if (availableDevices && availableDevices.length > 0) {
      return availableDevices.find(d => d.deviceId === deviceId) || null
    }
    return null
  },

  reset: () => {
    set({
      currentDeviceId: null,
      currentDeviceSerialId: null,
      availableDevices: [],
      automaticSmsConfiguration: null,
      currentDevice: null
    })
  },

  resetState: () => {
    localStorage.removeItem("deviceId")
    localStorage.removeItem("currentDeviceSerialId")
  },

  getAvailableDevices: () => {
    return get().availableDevices
  },

  // Getters
  isDeviceSelected: () => {
    return !!get().currentDeviceId
  },

  getData: (key: string) => {
    const { currentDevice } = get()
    if (currentDevice?.deviceConfiguration?.[key] !== undefined) {
      return currentDevice.deviceConfiguration[key]
    }
    return ""
  },

  isMdsDevice: () => {
    const { currentDevice } = get()
    return currentDevice?.type === "DEVICE_SELECTOR"
  },

  getCurrentDeviceData: () => {
    return get().currentDevice
  },

  getCurrentDeviceId: () => {
    return get().currentDeviceId
  },

  getCurrentDeviceSerialId: () => {
    return get().currentDeviceSerialId
  },

  getCurrentDevice: () => {
    return get().currentDevice
  },

  currentDeviceName: () => {
    const { currentDevice } = get()
    return currentDevice ? (currentDevice.customerName || currentDevice.name || 'Device') : 'Device'
  },
}))
